import { match } from 'ts-pattern'

import {
  isQuotePricingComparison,
  DEFAULT_QUOTE_COMPARISON_LABELS,
  PLURAL_QUOTE_COMPARISON_LABELS,
  QUOTE_PRICING_COMPARISONS,
  QUOTE_PRICING_COMPARISON_DISPLAY,
  readPricingComparison,
  type QuoteComparisonLabels,
  type QuoteComparisonSubOperator,
  type QuotePricingComparison
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'

/**
 * The fields a quote can be compared on, whatever it is measured against — a
 * template, or the version of itself that was last approved. One operator per
 * field; a new field joins this list without restructuring either editor.
 *
 * Ordered as the field dropdown reads: pricing is what a row usually asks
 * about, so it leads.
 */
export const QUOTE_COMPARISON_FIELDS = [
  'PRICING',
  'RAW_TEXT',
  'PAYMENT_TERMS',
  'ATTACHMENTS'
] as const

export type QuoteComparisonField = (typeof QUOTE_COMPARISON_FIELDS)[number]

export const isQuoteComparisonField = (
  operator: unknown
): operator is QuoteComparisonField =>
  QUOTE_COMPARISON_FIELDS.includes(operator as QuoteComparisonField)

/**
 * Only pricing splits into separate comparisons — all of it, or just its prices,
 * discounts or minimums — so only pricing shows that dropdown.
 */
export const hasPricingComparisons = (operator: string): boolean =>
  operator === 'PRICING'

/** The is-equal / is-not-equal a row opens on, so its dropdown is never blank. */
export const DEFAULT_SUB_OPERATOR: QuoteComparisonSubOperator = 'IS_EQUAL'

/** Attachments read as plural: "<subject> attachments are equal". */
export const ATTACHMENTS_COMPARISON_LABELS = PLURAL_QUOTE_COMPARISON_LABELS

/** Payment terms read as plural: "<subject> payment terms are equal". */
export const PAYMENT_TERMS_COMPARISON_LABELS = PLURAL_QUOTE_COMPARISON_LABELS

/**
 * A subject can be compared in many ways, but each way only once. These name
 * every way as a string, so used ones can be tracked and offered no further.
 *
 *   PRICING:ALL  PRICING:PRICES  PRICING:DISCOUNTS  PRICING:MINIMUMS
 *   RAW_TEXT     PAYMENT_TERMS   ATTACHMENTS
 */
export const expressionFor = ({
  operator,
  value
}: {
  operator: string
  value: unknown
}): string =>
  match(operator)
    .with('PRICING', () => `${operator}:${readPricingComparison(value)}`)
    .otherwise(() => operator)

const expressionsForField = (field: QuoteComparisonField): string[] =>
  match(field)
    .with('PRICING', () =>
      QUOTE_PRICING_COMPARISONS.map(comparison => `${field}:${comparison}`)
    )
    .otherwise(() => [field])

const ALL_EXPRESSIONS = QUOTE_COMPARISON_FIELDS.flatMap(expressionsForField)

/**
 * The expressions a subject has left, given what sibling rows already
 * contribute. The asking row's own expression stays listed even when a sibling
 * holds it, so a stored condition always renders against a dropdown that
 * contains its own value.
 */
export const getRemainingExpressions = ({
  usedExpressions,
  ownExpression
}: {
  usedExpressions: string[]
  ownExpression?: string
}): string[] =>
  ALL_EXPRESSIONS.filter(
    expression =>
      expression === ownExpression || !usedExpressions.includes(expression)
  )

/**
 * The expression a row already holds, which must stay offered. A pricing row
 * holds one only once a comparison is stored — until then it takes whichever
 * is free rather than claiming the whole pricing.
 */
export const ownExpressionOf = ({
  operator,
  comparison
}: {
  operator: string
  comparison: QuotePricingComparison | undefined
}): string | undefined => {
  if (hasPricingComparisons(operator)) {
    return comparison ? `PRICING:${comparison}` : undefined
  }
  return isQuoteComparisonField(operator) ? operator : undefined
}

/**
 * Splits an expression back into what a row stores. Pricing is the only field
 * that carries a comparison, so everything else parses to the field alone.
 *
 *   PRICING:PRICES -> { field: 'PRICING', comparison: 'PRICES' }
 *   RAW_TEXT       -> { field: 'RAW_TEXT' }
 */
export const parseExpression = (
  expression: string
): { field: QuoteComparisonField; comparison?: QuotePricingComparison } => {
  const [field, comparison] = expression.split(':')

  return {
    field: field as QuoteComparisonField,
    comparison: isQuotePricingComparison(comparison) ? comparison : undefined
  }
}

/**
 * The field dropdown's options: one entry per way of comparing, so pricing's
 * comparisons sit beside the other fields rather than behind a second
 * dropdown. Pricing names itself; the rest take their operator's label.
 *
 *   All contract terms  All prices  All discounts  All minimums  Raw text
 *   Payment terms       Attachments
 */
export const expressionOptions = ({
  remainingExpressions,
  operators
}: {
  remainingExpressions: string[]
  operators: Record<string, { label: string }>
}): Array<{ label: string; value: string }> =>
  remainingExpressions.map(expression => {
    const { field, comparison } = parseExpression(expression)

    return {
      label: comparison
        ? QUOTE_PRICING_COMPARISON_DISPLAY[comparison].label
        : (operators[field]?.label ?? field),
      value: expression
    }
  })

/** True once every field and comparison on a subject is taken by some row. */
export const isExhausted = (usedExpressions: string[]): boolean =>
  getRemainingExpressions({ usedExpressions }).length === 0

// Fields whose subject isn't singular override the default phrasing. Anything
// absent here falls back to "Is equal" / "Is not equal".
const COMPARISON_LABELS_BY_FIELD: Partial<
  Record<QuoteComparisonField, QuoteComparisonLabels>
> = {
  ATTACHMENTS: ATTACHMENTS_COMPARISON_LABELS,
  PAYMENT_TERMS: PAYMENT_TERMS_COMPARISON_LABELS
}

/**
 * The is-equal / is-not-equal labels a row reads with, which follow the number
 * of its subject. Pricing's subject is whichever comparison it chose — all of
 * them plural, since the whole of it reads as "contract terms":
 *
 *   pricing (ALL)    -> "are equal"
 *   pricing (PRICES) -> "are equal"
 *   payment terms    -> "are equal"
 *   raw text         -> "is equal"
 */
export const comparisonLabelsFor = ({
  operator,
  value
}: {
  operator: string
  value?: unknown
}): QuoteComparisonLabels => {
  if (hasPricingComparisons(operator)) {
    return QUOTE_PRICING_COMPARISON_DISPLAY[readPricingComparison(value)]
      .isPlural
      ? PLURAL_QUOTE_COMPARISON_LABELS
      : DEFAULT_QUOTE_COMPARISON_LABELS
  }

  return (
    COMPARISON_LABELS_BY_FIELD[operator as QuoteComparisonField] ??
    DEFAULT_QUOTE_COMPARISON_LABELS
  )
}

/** The is-equal / is-not-equal options, not the pricing comparisons. */
export const comparisonOptionsFor = (params: {
  operator: string
  value?: unknown
}): Array<{ label: string; value: QuoteComparisonSubOperator }> => {
  const labels = comparisonLabelsFor(params)
  return [
    { label: labels.isEqual, value: 'IS_EQUAL' },
    { label: labels.isNotEqual, value: 'IS_NOT_EQUAL' }
  ]
}
